import { ObservableMap } from "mobx";
import { CallStackResolution } from "../../lib/Causation/CallStackResolution";
import type { DomainMetadataDerivedFromUrl, NetworkEvent, RecordableEvent, HasWhenRecordedIso, HasEventIndex, RecordableEventCategory, RecordableEventName, UrlField, HasTab, RecordableEventNameWithinCategory } from "../../lib/Events";
import type { ExtensionSettingsPartial, EvidenceRecordingSettings } from "../../lib/MultiWorld/ExtensionSettings";
import type DTProto from "devtools-protocol";
import { Entry as HARFormatEntry, Log as HARFormatLog } from "har-format";

export interface Errorish {
	readonly name: string;
	readonly message: string;
	readonly stack?: string;
	readonly cause?: unknown;
}

export const isErrorish = (errorish: unknown): errorish is Errorish =>
	typeof errorish === "object" && errorish !== null &&
		"name" in errorish && "message" in errorish &&
		typeof errorish.name === "string" &&
		typeof errorish.message === "string" &&
		(!("stack" in errorish) || typeof errorish.stack === "string");

		
export type ISODateTimeString = `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;
export const ISODateTimeString = (date: Date = new Date) =>
	date.toISOString() as ISODateTimeString;

const isoDateTimeStringRegExp = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z$/;
export const isISODateTimeString = (candidate: unknown): candidate is ISODateTimeString =>
	typeof candidate === "string" && isoDateTimeStringRegExp.test(candidate);
export const nowAsISODateTimeString = () => new Date().toISOString() as ISODateTimeString;

const maxMinUUID = {
	min: "00000000-0000-0000-0000-000000000000" as const,
	max: "ffffffff-ffff-ffff-ffff-ffffffffffff" as const,
}
export type UUID = ReturnType<typeof crypto.randomUUID>;
const uuidRegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const isUUID = (candidate: unknown): candidate is UUID =>
	typeof candidate === "string" && uuidRegExp.test(candidate);
// UUID for use in insecure contexts where crypto.randomUUID is not available
// See https://github.com/lil-js/uuid/blob/master/uuid.js
const uuidV4 = () => {
	let uuid: string = '';
	for (let i = 0; i < 32; i++) {
		const random = Math.random() * 16 | 0;
		if (i === 8 || i === 12 || i === 16 || i === 20) uuid += '-'
		uuid += (i === 12 ? 4 : (i === 16 ? (random & 3 | 8) : random)).toString(16)
	}
	return uuid as UUID;
}
const getUuid = crypto?.randomUUID != null ? () => crypto.randomUUID() : uuidV4;
export const UUID: typeof crypto.randomUUID & {min: UUID, max: UUID} = Object.assign(getUuid, maxMinUUID);

export interface NetworkRequestStatistics {
	whenFirstObservedIso: ISODateTimeString;
	whenLastObservedIso: ISODateTimeString;
	numberOfRequests: number;
}

export type OptionallyObservableMap<K, V> = Map<K, V> | ObservableMap<K, V>;

export interface StatisticsForRequestsForUrl extends NetworkRequestStatistics {
	url: string;
}

export interface StatisticsForRequestsForDomain extends NetworkRequestStatistics {
	domain: string;
}

export interface StatisticsNodeForRequestsForDomainNode extends StatisticsForRequestsForDomain {
	urls: OptionallyObservableMap<string, StatisticsForRequestsForUrl>;
}

export interface StatisticsForRequestsForRegisteredDomain extends NetworkRequestStatistics {
	registeredDomain: string;
}

export interface StatisticsNodeForRequestsForRegisteredDomain extends StatisticsForRequestsForRegisteredDomain {
	domains: OptionallyObservableMap<string, StatisticsNodeForRequestsForDomainNode>;
}

export type NetworkOriginStatisticsTree = OptionallyObservableMap<string, StatisticsNodeForRequestsForRegisteredDomain>;

export interface RecordingNetworkStatisticsTree extends NetworkRequestStatistics {
	numberOfEvents: number;
	sourcePageOriginStatisticsTree: NetworkOriginStatisticsTree;
	destinationOriginStatisticsTree: NetworkOriginStatisticsTree;
}

export interface RecordingDataSummary extends NetworkRequestStatistics {
	numberOfEvents: number;
	sourcePageRegisteredDomains: StatisticsForRequestsForRegisteredDomain[];
	destinationRegisteredDomains: StatisticsForRequestsForRegisteredDomain[];
}

export interface HasRecordingUuid {
	recordingUuid: UUID;
}

export const RecordingStatusLiterals = {
	stopped: "stopped",
	archived: "archived",
//	allTabs: "allTabs",
	recording: "recording",
} as const;
export type RecordingStatus = typeof RecordingStatusLiterals[keyof typeof RecordingStatusLiterals];

export interface DbExtensionSettings {
	settingsIndex?: number;
	settings: ExtensionSettingsPartial;
}

export type DbRecordedEventTabField<EVENT extends RecordableEvent = RecordableEvent> =
	EVENT extends {tab: UrlField} ? {readonly tab: EVENT["tab"] & DomainMetadataDerivedFromUrl} : {}

export interface NetworkEventAugmentedFields {
	readonly destination: NetworkEvent["destination"] & DomainMetadataDerivedFromUrl
	readonly initiatorOrigin?: string | undefined;
}

export type DbRecordedNetworkEventFields<EVENT extends RecordableEvent = RecordableEvent> =
	EVENT extends NetworkEvent ? NetworkEventAugmentedFields : {};


export interface FieldsNeededToRecordEvent extends HasRecordingUuid, HasEventIndex, HasWhenRecordedIso {}


export type AugmentedNetworkEvent = NetworkEvent & DomainMetadataDerivedFromUrl;

export type EventReadyToWriteToDb<
	EVENT extends RecordableEvent = RecordableEvent
> = EVENT & HasTab & DbRecordedNetworkEventFields<EVENT>;

export type DbRecordedEvent<
	EVENT extends RecordableEvent = RecordableEvent
> = EventReadyToWriteToDb<EVENT> & FieldsNeededToRecordEvent

export type RecordedEvent<EVENT extends RecordableEvent = RecordableEvent> =
	DbRecordedEvent<EVENT>; 

export type DbRecordedEventByCategory<
	EVENT_CATEGORY extends RecordableEventCategory
> = Extract<DbRecordedEvent, {category: EVENT_CATEGORY}>;

export type DbRecordedEventByCategoryAndName<
	EVENT_CATEGORY extends RecordableEventCategory,
	 EVENT_NAME extends RecordableEventName,
> = Extract<DbRecordedEvent, {category: EVENT_CATEGORY, eventName: EVENT_NAME}>;

export type DbRecordedEventByName<
	EVENT_NAME extends RecordableEventName,
> = Extract<DbRecordedEvent, {eventName: EVENT_NAME}>;


export type RecordedEventByCategoryAndName<
CATEGORY extends RecordableEventCategory,
NAME extends RecordableEventNameWithinCategory<CATEGORY>
> = Extract<RecordedEvent, { category: CATEGORY; eventName: NAME; }>;

export interface EventMetadataParameter {
	initiator?: string | DTProto.Network.Initiator | undefined;
}


export interface DbHarEntry extends Readonly<HARFormatEntry>, HasRecordingUuid {
	readonly entryUuid: UUID;
}

export interface DbHarLog extends Readonly<HARFormatLog>, HasRecordingUuid {
	whenRecordedIso: ISODateTimeString;
}

export interface DbErrorLogEntry extends HasRecordingUuid, Errorish {
	errorUuid: UUID;
	errorTimeIso: ISODateTimeString;
	context?: string;
}

export interface DbVideoFragment extends HasRecordingUuid {
	videoFragmentUuid: UUID;
	data: Blob;
	timecode: DOMHighResTimeStamp;
	streamId: string;
}

export type DbMhtmlCapture = /* chrome.webNavigation.WebNavigationFramedCallbackDetails  & */
HasRecordingUuid & {
	url: string;
	timeStamp: number;
	mhtmlCaptureIndex: number;
	mhtmlCaptureUuid: UUID;
	mhtmlBlob: Blob;
	tabId: number;
};

export interface DbRecording extends HasRecordingUuid {
	status: RecordingStatus;
	startTimeIso: ISODateTimeString;
	stopTimeIso?: ISODateTimeString | null;
	name?: string | null;
	callStackResolution?: CallStackResolution | null;
	externallyVisibleIpAddress?: string | undefined;
	videoRecordingInProgress?: true;
	searchTermsNewlineSeparated?: string | null;
	globalSearchTermsNewlineSeparated?: string | null;
	domGeneratedSearchTermsToIgnore?: string[] | null;
	/**
	 * Invalidate when dataSummary.numberOfEvents !== EventsTable.countForRecordingUuid(recordingUuid)
	 */
	dataSummary?: RecordingDataSummary | null;
	settings: EvidenceRecordingSettings;
}

export interface DbTabBeingRecorded {
	tabId: number;
	recordingUuid: UUID;
	recordingShouldEndWhenThisTabStopsRecording: boolean;
	startTimeIso: ISODateTimeString;
}


export type RecordingsTableIndexForStatus = {
	'RecordingsTable-Index-Status': [RecordingStatus];
}
export type NameOfRecordingsTableIndexForStatus = keyof RecordingsTableIndexForStatus;
export type TypesForRecordingsTableIndexForStatus = RecordingsTableIndexForStatus[NameOfRecordingsTableIndexForStatus];
export const NameOfRecordingsTableIndexForStatus = "RecordingsTable-Index-Status" satisfies NameOfRecordingsTableIndexForStatus;


export type TabBeingRecordedTableIndexForRecordingUuid = {
	'TabBeingRecordedTable-Index-RecordingUuid': [UUID];
}
export type NameOfTabBeingRecordedTableIndexForRecordingUuid = keyof TabBeingRecordedTableIndexForRecordingUuid;
export type TypesForTabBeingRecordedTableIndexForRecordingUuid = TabBeingRecordedTableIndexForRecordingUuid[NameOfTabBeingRecordedTableIndexForRecordingUuid];
export const NameOfTabBeingRecordedTableIndexForRecordingUuid = "TabBeingRecordedTable-Index-RecordingUuid" satisfies NameOfTabBeingRecordedTableIndexForRecordingUuid;



export const eventsFileFormatVersion = 1;

export interface MyDataGoWhereEventArchiveFormat extends Exclude<DbRecording, 'status'> {
	version: number;
	events: DbRecordedEvent[];
}
export const isMyDataGoWhereEventArchiveFormat = (candidate: unknown): candidate is MyDataGoWhereEventArchiveFormat =>
	typeof candidate === "object" && candidate !== null &&
		"version" in candidate && typeof candidate.version === "number" &&
		"events" in candidate && Array.isArray(candidate.events);

export type ErrorLogTableIndexForRecordingUuidThenErrorTime = {
	'ErrorLogTable-Index-RecordingUuidThenErrorTime': [UUID, ISODateTimeString];
}
export type NameOfErrorLogTableIndexForRecordingUuidThenErrorTime = keyof ErrorLogTableIndexForRecordingUuidThenErrorTime;
export type TypesForErrorLogTableIndexForRecordingUuidThenErrorTime = ErrorLogTableIndexForRecordingUuidThenErrorTime[NameOfErrorLogTableIndexForRecordingUuidThenErrorTime];
export const NameOfErrorLogTableIndexForRecordingUuidThenErrorTime = "ErrorLogTable-Index-RecordingUuidThenErrorTime" satisfies NameOfErrorLogTableIndexForRecordingUuidThenErrorTime;


export type HarEntryTableIndexForRecordingUuidStartTime = {
	'HarEntryTable-Index-RecordingUuidStartTime': [UUID, ISODateTimeString];
}
export type NameOfHarEntryTableIndexForRecordingUuidStartTime = keyof HarEntryTableIndexForRecordingUuidStartTime;
export type TypesForHarEntryTableIndexForRecordingUuidStartTime = HarEntryTableIndexForRecordingUuidStartTime[NameOfHarEntryTableIndexForRecordingUuidStartTime];
export const NameOfHarEntryTableIndexForRecordingUuidStartTime = "HarEntryTable-Index-RecordingUuidStartTime" satisfies NameOfHarEntryTableIndexForRecordingUuidStartTime;

export type VideoFragmentsTableIndexForRecordingUuidThenStreamIdThenTimecode = {
	'RecordingUuid-StreamId-Timecode': [UUID, UUID, number];
}
export type NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode = keyof VideoFragmentsTableIndexForRecordingUuidThenStreamIdThenTimecode;
export type TypesForVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode = VideoFragmentsTableIndexForRecordingUuidThenStreamIdThenTimecode[NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode];
export const NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode = "RecordingUuid-StreamId-Timecode" satisfies NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode;

export type MhtmlCapturesTableIndexForRecordingUuidThenMhtmlCaptureUuid = {
	'RecordingUuid-MhtmlCaptureUuid': [UUID, UUID];
}
export type NameOfIndexForRecordingUuidThenMhtmlCaptureUuid = keyof MhtmlCapturesTableIndexForRecordingUuidThenMhtmlCaptureUuid;
export type TypesForIndexForRecordingUuidThenMhtmlCaptureUuid = MhtmlCapturesTableIndexForRecordingUuidThenMhtmlCaptureUuid[NameOfIndexForRecordingUuidThenMhtmlCaptureUuid];
export const NameOfIndexForRecordingUuidThenMhtmlCaptureUuid = "RecordingUuid-MhtmlCaptureUuid" satisfies NameOfIndexForRecordingUuidThenMhtmlCaptureUuid;

// export type EventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex = {
// 	"RecordingUuid-Category-Name-DestinationUrl-Index": [UUID, RecordableEventCategory, RecordableEventName, string, number];
// }
// export const EventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex = "RecordingUuid-Category-Name-DestinationUrl-Index";
// export type NameOfEventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex = keyof EventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex;
// export const NameOfEventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex = "RecordingUuid-Category-Name-DestinationUrl-Index" satisfies NameOfEventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex;

export type EventsTableIndexForRecordingUuidThenCategoryThenNameThenNumericIndex = {
	"RecordingUuid-Category-Name-Index": [UUID, RecordableEventCategory, RecordableEventName, number];
}
export type NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex = keyof EventsTableIndexForRecordingUuidThenCategoryThenNameThenNumericIndex;
export const NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex = "RecordingUuid-Category-Name-Index" satisfies NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex;

export type EventsTableIndexForRecordingUuidThenRequestIdThenCategoryThenName = {
	"RecordingUuid-RequestId-Category-Name": [UUID, string, RecordableEventCategory, RecordableEventName];
}
export type NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName = keyof EventsTableIndexForRecordingUuidThenRequestIdThenCategoryThenName;
export const NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName = "RecordingUuid-RequestId-Category-Name" satisfies NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName;

export type EventsTableIndexForReceivedEventUuidThenEventName = {
	'receivedEventUuid-eventName': [UUID, RecordableEventName, number];
}
export type NameOfIndexForReceivedEventUuidThenEventName = keyof EventsTableIndexForReceivedEventUuidThenEventName;
export type TypesForIndexForReceivedEventUuidThenEventName = EventsTableIndexForReceivedEventUuidThenEventName[NameOfIndexForReceivedEventUuidThenEventName];
export const NameOfIndexForReceivedEventUuidThenEventName = 'receivedEventUuid-eventName' satisfies NameOfIndexForReceivedEventUuidThenEventName;

export type EventsTableIndexForEventListenerUuidThenEventName = {
	'eventListenerUuid-eventName': [UUID, RecordableEventName, number];
}
export type NameOfIndexForEventListenerUuidThenEventName = keyof EventsTableIndexForEventListenerUuidThenEventName;
export type TypesForIndexForEventListenerUuidThenEventName = EventsTableIndexForEventListenerUuidThenEventName[NameOfIndexForEventListenerUuidThenEventName];
export const NameOfIndexForEventListenerUuidThenEventName = 'eventListenerUuid-eventName' satisfies NameOfIndexForEventListenerUuidThenEventName;

	

export const getRangeForAllRecordsWithRecordingUuidAndRowUuid = (recordingUuid: UUID) =>
	IDBKeyRange.bound([recordingUuid, UUID.min], [recordingUuid, UUID.max]);

export const getRangeForAllRecordsWithRecordingUuidAndRowIndex = (recordingUuid: UUID) =>
	IDBKeyRange.bound([recordingUuid, Number.MIN_SAFE_INTEGER], [recordingUuid, Number.MAX_SAFE_INTEGER]);

